// Import directly (not via the ../typesense barrel) so the browser bundle does not
// transitively pull in server-only modules like to-typesense-job-record (node:crypto).
import {
  buildTypesenseSearchParams,
  type AlgoliaLikeSearchParams,
} from './build-typesense-search-params';
import { getTypesenseJobsCollectionForAlgoliaIndex } from './algolia-index-to-collection';
import { mapTypesenseDocumentToAlgoliaHit } from './map-typesense-document-to-algolia-hit';
import { mapTypesenseResponseToAlgoliaShape } from './map-typesense-response-to-algolia-shape';

const DEFAULT_BASE_URL = '';

export interface TypesenseBrowserSearchRequest {
  indexName: string;
  params: AlgoliaLikeSearchParams;
}

export interface TypesenseBrowserSearchClient {
  search: (
    requests: TypesenseBrowserSearchRequest[],
  ) => Promise<{ results: ReturnType<typeof mapTypesenseResponseToAlgoliaShape>[] }>;
  initIndex: (indexName: string) => {
    getObjects: (objectIDs: string[]) => Promise<{ results: unknown[] }>;
  };
}

async function postTypesenseSearch(
  collection: string,
  params: AlgoliaLikeSearchParams,
  indexName: string,
  proxyPath: string,
): Promise<ReturnType<typeof mapTypesenseResponseToAlgoliaShape>> {
  const typesenseParams = buildTypesenseSearchParams(params, { indexName });
  const res = await fetch(proxyPath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ collection, params: typesenseParams }),
  });
  if (!res.ok) {
    throw new Error(`Typesense search failed: ${res.status}`);
  }
  const data = (await res.json()) as { response: Record<string, unknown> };
  return mapTypesenseResponseToAlgoliaShape(
    data.response as Parameters<typeof mapTypesenseResponseToAlgoliaShape>[0],
    params.query ?? '',
  );
}

/**
 * Browser search client with Algolia-compatible `search()` for SearchContext.
 * All requests go through the server proxy — search key never exposed to browser.
 *
 * `baseUrl` lets local dev point at the deployed API (e.g. https://www.jobcrawls.com) so
 * Typesense search works without a local Typesense instance/tunnel — CORS on /api/* already
 * allows localhost origins for exactly this. Defaults to '' (same-origin, used in prod).
 */
export function createTypesenseBrowserSearchClient(options?: {
  baseUrl?: string;
}): TypesenseBrowserSearchClient {
  const baseUrl = options?.baseUrl ?? DEFAULT_BASE_URL;
  const proxyPath = `${baseUrl}/api/typesense/search`;
  const documentsPath = `${baseUrl}/api/typesense/documents`;

  return {
    async search(requests) {
      const results = await Promise.all(
        requests.map(async (req) => {
          const collection = getTypesenseJobsCollectionForAlgoliaIndex(req.indexName);
          if (!collection) {
            throw new Error(`Unknown Algolia index for Typesense mapping: ${req.indexName}`);
          }
          return postTypesenseSearch(collection, req.params, req.indexName, proxyPath);
        }),
      );
      return { results };
    },

    initIndex(indexName: string) {
      return {
        async getObjects(objectIDs: string[]) {
          const collection = getTypesenseJobsCollectionForAlgoliaIndex(indexName);
          if (!collection) {
            return { results: [] };
          }
          const res = await fetch(documentsPath, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ collection, ids: objectIDs }),
          });
          if (!res.ok) {
            return { results: [] };
          }
          const data = (await res.json()) as { documents: Record<string, unknown>[] };
          return {
            results: (data.documents ?? [])
              .map((doc) => mapTypesenseDocumentToAlgoliaHit(doc))
              .filter((hit): hit is Record<string, unknown> => hit !== null),
          };
        },
      };
    },
  };
}
